/**
 * Ant Design Pro v4 use `@ant-design/pro-layout` to handle Layout.
 * You can view component api by:
 * https://github.com/ant-design/ant-design-pro-layout
 */
import React, { useCallback, useEffect } from "react";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import ProLayout, { DefaultFooter } from "@ant-design/pro-layout";
import { Button, Result } from "antd";
import { connect } from "dva";
import { Link } from "umi";
import { formatMessage } from "umi-plugin-react/locale";
import { COMPANY_DOMAIN, COMPANY_PHONE, COMPANY_PRIVACY_POLICY_URL, COMPANY_SUPPORT_EMAIL } from "@/utils/constants";
import { getFirstAccessibleRoute } from "@/utils/route";
import { useDatadog } from "@/utils/setupDatadog";
import { getAuthorityFromRouter } from "@/utils/utils";
import useScreenDetector from "@/hooks/useScreenDetector";
import Authorized from "@/components/Authorized";
import BreadcrumbItem from "@/components/BreadcrumbItem";
import RightContent from "@/components/GlobalHeader/RightContent";
import PageLoading from "@/pages/Dashboard/components/PageLoading";
const logoSizes = {
    collapsed: {
        menuHidden: "32px",
        smallMenu: "32px",
        largeMenu: "32px",
    },
    expanded: {
        menuHidden: "100px",
        smallMenu: "140px",
        largeMenu: "140px",
    },
};
const logoWrapperSizes = {
    collapsed: {
        menuHidden: "64px",
        smallMenu: "80px",
        largeMenu: "80px",
    },
    expanded: {
        menuHidden: "208px",
        smallMenu: "280px",
        largeMenu: "280px",
    },
};
const noMatch = (React.createElement(Result, { status: "403", title: "403", subTitle: "Sorry, you are not authorized to access this page.", extra: React.createElement(Button, { type: "primary" },
        React.createElement(Link, { to: "/user/login" }, "Go Login")) }));
const menuItemRender = (menuItemProps, defaultDom) => {
    if (menuItemProps.isUrl || menuItemProps.children || !menuItemProps.path) {
        return defaultDom;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const entityId = urlParams.get("entityId");
    if (entityId) {
        return React.createElement(Link, { to: `${menuItemProps.path}?entityId=${entityId}` }, defaultDom);
    }
    return React.createElement(Link, { to: menuItemProps.path }, defaultDom);
};
const rightContentRender = ({ history }) => React.createElement(RightContent, { history: history });
/**
 * use Authorized check all menu item
 */
const menuDataRender = (menuList, configPerm) => menuList.map(item => {
    const localItem = {
        ...item,
        children: item.children ? menuDataRender(item.children, configPerm) : [],
    };
    // @ts-ignore it's correct
    return Authorized.check(item.authority, localItem, configPerm, null);
});
const footerRender = () => (React.createElement(DefaultFooter, { copyright: "2020 Autosled", links: [
        { key: "Autosled", title: "Autosled", href: COMPANY_DOMAIN },
        { key: "Phone", title: COMPANY_PHONE, href: `tel:${COMPANY_PHONE}` },
        { key: "Privacy", title: "Privacy", href: COMPANY_PRIVACY_POLICY_URL },
        { key: "Help", title: "Help", href: `mailto:${COMPANY_SUPPORT_EMAIL}` },
    ] }));
const BasicLayout = (props) => {
    const { loading, dispatch, children, settings, configPerm, route, collapsed, location = {
        pathname: "/",
    }, history, } = props;
    const { isTablet: smallMenu, isDesktop: largeMenu } = useScreenDetector(768, 992);
    const firstAccessibleRoute = getFirstAccessibleRoute((route?.routes || []), configPerm);
    const breadcrumbRender = useCallback(routes => {
        if (Array.isArray(routes)) {
            if (routes.length === 1 && routes[0].path === firstAccessibleRoute) {
                return routes;
            }
            return [
                {
                    path: firstAccessibleRoute,
                    breadcrumbName: "Home",
                },
                ...routes,
            ];
        }
        return undefined;
    }, [firstAccessibleRoute]);
    const menuHeaderRender = useCallback(() => {
        let key = "menuHidden";
        if (smallMenu) {
            key = "smallMenu";
        }
        else if (largeMenu) {
            key = "largeMenu";
        }
        const iconSize = logoSizes[collapsed ? "collapsed" : "expanded"][key];
        const wrapperWidth = logoWrapperSizes[collapsed ? "collapsed" : "expanded"][key];
        return (React.createElement(Link, { to: "/", style: { display: "flex", justifyContent: "center", alignItems: "center", width: wrapperWidth } },
            React.createElement("img", { src: "/img/AutosledLogoDarkSilver.png", alt: "Autosled logo", style: {
                    width: iconSize,
                    height: iconSize,
                } })));
    }, [smallMenu, largeMenu, collapsed]);
    useEffect(() => {
        if (dispatch) {
            dispatch({
                type: "user/fetchCurrentProfile",
            });
        }
    }, [dispatch]);
    useDatadog();
    const handleMenuCollapse = useCallback(payload => {
        if (dispatch) {
            dispatch({
                type: "global/changeLayoutCollapsed",
                payload,
            });
        }
    }, [dispatch]);
    const headerContentRender = useCallback(() => (React.createElement("span", { role: "button", tabIndex: 0, style: {
            width: "60px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            fontSize: 20,
        }, onClick: () => handleMenuCollapse(!collapsed) }, collapsed ? React.createElement(MenuUnfoldOutlined, null) : React.createElement(MenuFoldOutlined, null))), [collapsed, handleMenuCollapse]);
    const authorized = getAuthorityFromRouter(route?.routes, location.pathname || firstAccessibleRoute) || {
        authority: undefined,
    };
    if (loading) {
        return React.createElement(PageLoading, null);
    }
    return (React.createElement(ProLayout, { formatMessage: formatMessage, headerContentRender: headerContentRender, collapsedButtonRender: () => null, menuHeaderRender: menuHeaderRender, onCollapse: handleMenuCollapse, menuItemRender: menuItemRender, breadcrumbRender: breadcrumbRender, itemRender: BreadcrumbItem, footerRender: footerRender, menuDataRender: menuList => menuDataRender(menuList, configPerm), rightContentRender: () => rightContentRender({ history }), ...props, ...settings },
        React.createElement(Authorized, { authority: authorized.authority, noMatch: noMatch, configPerm: configPerm }, children)));
};
export default connect(({ global, settings, loading, configs, }) => ({
    configPerm: configs.configPerm,
    collapsed: global.collapsed,
    settings,
    loading: loading.effects["configs/getConfigData"] ||
        loading.effects["user/fetchCurrentProfile"] ||
        loading.effects["configs/validateDevKey"],
}))(BasicLayout);
